import { FC, useCallback, useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { AlertDialogWrapper } from '../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { CloseModalButton } from '../../../components/close-modal-button/index';
import { Sheet, SheetContent } from '../../../components/sheet/index';
import {
  DividendFrequency,
  DividendPayout,
  DividendPayoutType,
} from '../../../hooks/dividend/dividend.types';
import { DividendHooks, useReactForm } from '../../../hooks/index';
import { getTranslation } from '../../../utils/get-translation.util';
import { AddDividendFormData, addDividendSchema } from './add-dividend.validation';
import { AddDividendStepFive } from './add-dividend-step-five';
import { AddDividendStepFour } from './add-dividend-step-four';
import { AddDividendStepOne } from './add-dividend-step-one';
import { AddDividendStepThree } from './add-dividend-step-three';
import { AddDividendStepTwo } from './add-dividend-step-two';
import { DividendUpdateConfirmModal } from './dividend-update-confirm-modal';
import { EditDividendSummary } from './edit-dividend-summary';

type Props = {
  companyId: string;
  currencySymbol: string;
  editPayout?: DividendPayout;
  isOpen: boolean;
  onClose: () => void;
  onEditDuplicate: (payout: DividendPayout) => void;
};

const TOTAL_STEPS = 5;

// Editing opens on a summary of the payout instead of dropping the user at step 1: most edits
// target one part of it. Picking a block enters the normal flow, so back/next still work from
// there (design 4937:31921).
const EDIT_SUMMARY_STEP = 0;

const getEmptyFormValues = () => ({
  additionalNotes: '',
  filesLinks: [],
  frequency: undefined,
  items: [],
  name: '',
  periodIndex: null,
  stakeholderDocuments: [],
  type: undefined,
  year: new Date().getFullYear(),
});

const getPeriodLabel = (frequency?: DividendFrequency, index?: null | number): string => {
  switch (frequency) {
    case DividendFrequency.MONTHLY:
      return index ? new Date(0, index - 1).toLocaleString('en-US', { month: 'long' }) : '';
    case DividendFrequency.QUARTERLY:
      return index ? `Q${index}` : '';
    case DividendFrequency.SEMI_ANNUALLY:
      return index === 1 ? 'H1' : index === 2 ? 'H2' : '';
    default:
      return '';
  }
};

export const AddDividendModal: FC<Props> = ({
  companyId,
  currencySymbol,
  editPayout,
  isOpen,
  onClose,
  onEditDuplicate,
}) => {
  const isEditMode = Boolean(editPayout);
  const [currentStep, setCurrentStep] = useState(1);
  // Carries the payout id, not just the form values: confirming happens in a dialog outside this
  // sheet, and the parent may already have dropped `editPayout` by the time we submit.
  const [pendingUpdate, setPendingUpdate] = useState<{
    data: AddDividendFormData;
    id: string;
  } | null>(null);

  const { control, reset, handleSubmit, setValue, getValues } = useReactForm({
    schema: addDividendSchema,
    defaultValues: getEmptyFormValues(),
  });

  const watchedType = useWatch({ control, name: 'type' });
  const watchedFrequency = useWatch({ control, name: 'frequency' });
  const watchedPeriodIndex = useWatch({ control, name: 'periodIndex' });
  const watchedYear = useWatch({ control, name: 'year' });

  // Everywhere but step 1 the header carries the chosen distribution + period as a subtitle
  // (e.g. "Per Share Class · Q3 2026"), mirroring the design — including the edit summary.
  const headerSubtitle =
    currentStep !== 1 && watchedType
      ? [
          getTranslation(
            watchedType === DividendPayoutType.PER_SHARE_CLASS
              ? 'PER_SHARE_CLASS'
              : 'PER_STAKEHOLDER',
          ),
          [getPeriodLabel(watchedFrequency, watchedPeriodIndex), watchedYear]
            .filter(Boolean)
            .join(' '),
        ]
          .filter(Boolean)
          .join(' · ')
      : '';

  // Reset the form on every open so a fresh "Add Dividend" is empty and Edit shows its payout,
  // instead of retaining the previously entered values until a full page reload.
  useEffect(() => {
    if (!isOpen) return;

    setCurrentStep(editPayout ? EDIT_SUMMARY_STEP : 1);

    if (!editPayout) {
      reset(getEmptyFormValues());
      return;
    }

    reset({
      additionalNotes: editPayout.additionalNotes ?? '',
      filesLinks: editPayout.filesLinks ?? [],
      frequency: editPayout.frequency,
      items: editPayout.items.map((item) => ({
        amountPerShare: item.amountPerShare ?? undefined,
        shareClassId: item.shareClassId ?? undefined,
        shareClassName: undefined,
        stakeholderId: item.stakeholderId ?? undefined,
        stakeholderName: undefined,
        totalAmount: item.totalAmount ?? undefined,
      })),
      name: editPayout.name,
      periodIndex: editPayout.periodIndex,
      stakeholderDocuments: (editPayout.stakeholderDocuments ?? []).map((document) => ({
        filesLinks: document.filesLinks,
        fullName: '',
        stakeholderId: document.stakeholderId,
      })),
      type: editPayout.type,
      year: editPayout.year,
    });
  }, [editPayout, isOpen, reset]);

  const { create, isPending: isCreating } = DividendHooks.useDividendCreate();
  const { update, isPending: isUpdating } = DividendHooks.useDividendUpdate();
  const isPending = isCreating || isUpdating;

  const handleClose = useCallback(() => {
    reset();
    setCurrentStep(1);
    onClose();
  }, [onClose, reset]);

  const handleNext = useCallback(() => setCurrentStep((s) => Math.min(s + 1, TOTAL_STEPS)), []);

  const handleBack = useCallback(
    () => setCurrentStep((s) => (isEditMode && s <= 1 ? EDIT_SUMMARY_STEP : Math.max(s - 1, 1))),
    [isEditMode],
  );

  const buildPayload = useCallback((data: AddDividendFormData) => {
    const isShareClass = data.type === DividendPayoutType.PER_SHARE_CLASS;
    const apiItems = data.items
      .filter((item) =>
        isShareClass
          ? item.shareClassId && item.amountPerShare
          : item.shareClassId && item.stakeholderId && item.amountPerShare,
      )
      .map((item) =>
        isShareClass
          ? {
              amountPerShare: item.amountPerShare,
              shareClassId: item.shareClassId,
              totalAmount: item.totalAmount ?? undefined,
            }
          : {
              amountPerShare: item.amountPerShare ?? undefined,
              shareClassId: item.shareClassId,
              stakeholderId: item.stakeholderId,
              totalAmount: item.totalAmount,
            },
      );

    return {
      additionalNotes: data.additionalNotes || undefined,
      filesLinks: data.filesLinks,
      frequency: data.frequency,
      items: apiItems,
      name: data.name,
      periodIndex: data.periodIndex,
      stakeholderDocuments: data.stakeholderDocuments.map(({ filesLinks, stakeholderId }) => ({
        filesLinks,
        stakeholderId,
      })),
      type: data.type,
      year: data.year,
    };
  }, []);

  const handleCreate = useCallback(() => {
    handleSubmit((data: AddDividendFormData) => {
      const payload = buildPayload(data);
      create(payload, {
        onSuccess: () => {
          toast.success(getTranslation('DIVIDEND_CREATED_SUCCESS'));
          handleClose();
        },
      });
    })();
  }, [buildPayload, create, handleClose, handleSubmit]);

  // Validate before asking for confirmation, not after: `handleSubmit` swallows an invalid form,
  // so confirming first would leave the dialog open with nothing happening.
  const handleRequestUpdate = useCallback(() => {
    if (!editPayout) return;

    handleSubmit(
      (data: AddDividendFormData) => setPendingUpdate({ data, id: editPayout.id }),
      // The offending field lives on an earlier step, so say so instead of leaving a dead button.
      () => toast.error(getTranslation('DIVIDEND_INCOMPLETE_FOR_SAVE')),
    )();
  }, [editPayout, handleSubmit]);

  const handleConfirmUpdate = useCallback(() => {
    if (!pendingUpdate) return;

    update(
      { id: pendingUpdate.id, payload: buildPayload(pendingUpdate.data) },
      {
        // Either way the dialog closes: on failure the user lands back on the form, where the
        // error toast and any field errors are visible.
        onError: () => setPendingUpdate(null),
        onSuccess: () => {
          toast.success(getTranslation('DIVIDEND_UPDATED_SUCCESS'));
          setPendingUpdate(null);
          handleClose();
        },
      },
    );
  }, [buildPayload, handleClose, pendingUpdate, update]);

  return (
    <>
      <Sheet onOpenChange={(open) => !open && handleClose()} open={isOpen}>
        <SheetContent
          className="w-full max-w-[485px] border-transparent bg-transparent p-2 shadow-none"
          // The confirm dialog lives outside this sheet, so clicking it reads as an outside
          // interaction — closing the sheet from under the dialog the user is answering.
          onInteractOutside={(event) => {
            if (pendingUpdate) {
              event.preventDefault();
              return;
            }
            handleClose();
          }}
          side="RIGHT"
        >
          <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border-[1px] border-gray-300 bg-gray-100 pb-4">
            <div className="flex h-[72px] w-full items-center justify-between bg-white px-6">
              <div className="flex flex-col">
                <span className="text-xl font-semibold leading-8 text-gray-700">
                  <AppFormattedMessage id={isEditMode ? 'EDIT_DIVIDEND' : 'ADD_DIVIDEND'} />
                </span>
                {headerSubtitle && (
                  <span className="text-xs font-[450] leading-4 text-gray-500">
                    {headerSubtitle}
                  </span>
                )}
              </div>
              <CloseModalButton onClose={handleClose} />
            </div>

            {/* Editing is not a linear wizard — it starts from the summary, so there is no progress
              to show. */}
            {!isEditMode && (
              <div className="flex w-full items-center gap-[5px] bg-white px-6 pb-2 pt-2">
                {Array.from({ length: TOTAL_STEPS }, (_, i) => (
                  <div
                    className={twMerge(
                      'h-[3px] w-full rounded-[25px]',
                      i + 1 < currentStep && 'bg-[#12b76a]',
                      i + 1 === currentStep &&
                        'bg-gradient-to-r from-[#12b76a] from-[38.5%] to-[#6ce9a6]',
                      i + 1 > currentStep && 'bg-gray-100',
                    )}
                    key={i}
                  />
                ))}
              </div>
            )}

            <div className="flex h-full flex-col gap-4 overflow-hidden">
              {currentStep === EDIT_SUMMARY_STEP && (
                <EditDividendSummary
                  companyId={companyId}
                  control={control}
                  onCancel={handleClose}
                  onSelectStep={setCurrentStep}
                />
              )}
              {currentStep === 1 && (
                <AddDividendStepOne
                  companyId={companyId}
                  control={control}
                  editingPayoutId={editPayout?.id}
                  onBack={isEditMode ? handleBack : undefined}
                  onCancel={handleClose}
                  onEditDuplicate={onEditDuplicate}
                  onNext={handleNext}
                />
              )}
              {currentStep === 2 && (
                <AddDividendStepTwo
                  companyId={companyId}
                  control={control}
                  currencySymbol={currencySymbol}
                  onBack={handleBack}
                  onCancel={handleClose}
                  onNext={handleNext}
                  setValue={setValue}
                />
              )}
              {currentStep === 3 && (
                <AddDividendStepThree
                  companyId={companyId}
                  control={control}
                  getValues={getValues}
                  onBack={handleBack}
                  onCancel={handleClose}
                  onNext={handleNext}
                  setValue={setValue}
                />
              )}
              {currentStep === 4 && (
                <AddDividendStepFour
                  control={control}
                  getValues={getValues}
                  onBack={handleBack}
                  onCancel={handleClose}
                  onNext={handleNext}
                  setValue={setValue}
                />
              )}
              {currentStep === 5 && (
                <AddDividendStepFive
                  companyId={companyId}
                  control={control}
                  currencySymbol={currencySymbol}
                  isEditMode={isEditMode}
                  isSubmitting={isPending}
                  onBack={handleBack}
                  onCancel={handleClose}
                  onSubmit={isEditMode ? handleRequestUpdate : handleCreate}
                />
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialogWrapper
        control={{
          onOpenChange: (open) => !open && setPendingUpdate(null),
          open: pendingUpdate !== null,
        }}
      >
        <DividendUpdateConfirmModal
          isPending={isPending}
          onClose={() => setPendingUpdate(null)}
          onSuccess={handleConfirmUpdate}
        />
      </AlertDialogWrapper>
    </>
  );
};
